import React, { useEffect, useMemo, useRef } from 'react';
import { BrowserDOMPurify } from '../utils/dompurify';

interface SanitizedHtmlProps {
  content: string;
  className?: string;
}

/**
 * This component provides a secure, centralized boundary for HTML rendering.
 * It automatically sanitizes all content using DOMPurify before rendering,
 * ensuring that no malicious scripts or dangerous HTML can be executed.
 */
export const SanitizedHtml = React.memo<SanitizedHtmlProps>(({ content, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Sanitize content with DOMPurify
  const sanitizedContent = useMemo(() => BrowserDOMPurify.sanitize(content || ''), [content]);

  useEffect(() => {
    if (containerRef.current) {
      // Direct DOM manipulation with sanitized content by DOMPurify
      containerRef.current.innerHTML = sanitizedContent;
    }
  }, [sanitizedContent]);

  return <div ref={containerRef} className={className} />;
});

SanitizedHtml.displayName = 'SanitizedHtml';
